// "Opinie" tab: searchable, paginated review list.
import { CircleAlert, Download, RefreshCw, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReviewCard from "./ReviewCard";
import { SORT_OPTIONS } from "./constants";
import { EmptyState, Pagination } from "./ui";
import { nf, reviewsWord, scrollBelowSticky } from "./utils";

const ReviewsTab = ({
  list,
  loading,
  error,
  filters,
  sort,
  pageSize,
  onSort,
  onPageSize,
  onPage,
  onFilter,
  onSearch,
  onExport,
  exporting,
  onRetry,
}) => {
  const [searchInput, setSearchInput] = useState(filters.search);
  const topRef = useRef(null);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);
  useEffect(() => {
    if (searchInput === filters.search) return undefined;
    const t = setTimeout(() => onSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput, filters.search, onSearch]);

  const changePage = (p) => {
    onPage(p);
    scrollBelowSticky(topRef.current);
  };
  const toggle = (key, value) =>
    onFilter({ [key]: filters[key] === value ? "" : value });

  return (
    <div className="gr-tab" ref={topRef}>
      <div className="gr-reviews-toolbar">
        <label className="gr-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            placeholder="Szukaj w treści, autorze, mocnych i słabych stronach…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Szukaj opinii"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              aria-label="Wyczyść wyszukiwanie"
            >
              <X size={14} />
            </button>
          )}
        </label>
        <div className="gr-reviews-controls">
          <label className="gr-field gr-field-inline">
            <span>Sortuj</span>
            <select value={sort} onChange={(e) => onSort(e.target.value)}>
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="gr-btn gr-btn-ghost"
            onClick={onExport}
            disabled={exporting || !list?.total}
            title="Eksportuj przefiltrowane opinie do CSV"
          >
            <Download size={14} /> {exporting ? "Eksport…" : "CSV"}
          </button>
        </div>
      </div>

      <div
        className="gr-quick-toggles"
        role="group"
        aria-label="Szybkie filtry"
      >
        <button
          type="button"
          className={filters.sentiment === "negatywna" ? "is-active" : ""}
          onClick={() => toggle("sentiment", "negatywna")}
        >
          Negatywne
        </button>
        <button
          type="button"
          className={filters.hasText === "true" ? "is-active" : ""}
          onClick={() => toggle("hasText", "true")}
        >
          Z treścią
        </button>
        <button
          type="button"
          className={filters.hasReply === "false" ? "is-active" : ""}
          onClick={() => toggle("hasReply", "false")}
        >
          Bez odpowiedzi firmy
        </button>
        <button
          type="button"
          className={filters.urgency === "wysoki" ? "is-active" : ""}
          onClick={() => toggle("urgency", "wysoki")}
        >
          Wysoka pilność
        </button>
        <button
          type="button"
          className={filters.rating === "1-2" ? "is-active" : ""}
          onClick={() => toggle("rating", "1-2")}
        >
          1–2 ★
        </button>
      </div>

      <div className="gr-results-bar">
        <span>
          {list ? (
            <>
              <strong>{nf(list.total)}</strong> {reviewsWord(list.total)}
            </>
          ) : (
            "…"
          )}
        </span>
        <label className="gr-field gr-field-inline">
          <span>Na stronie</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
          >
            {[10, 20, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <div className="gr-alert gr-alert-error">
          <CircleAlert size={16} /> {error}
          <button
            type="button"
            className="gr-btn gr-btn-ghost"
            onClick={onRetry}
          >
            <RefreshCw size={14} /> Ponów
          </button>
        </div>
      ) : !list && loading ? (
        <div className="gr-skeleton-list" />
      ) : list?.items?.length ? (
        <div className={`gr-review-list ${loading ? "is-loading" : ""}`}>
          {list.items.map((item) => (
            <ReviewCard
              key={item.id}
              item={item}
              onFilter={onFilter}
              query={filters.search}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title="Brak opinii dla wybranych filtrów"
          text="Zmień okres lub usuń część filtrów."
        />
      )}

      {list && (
        <Pagination page={list.page} pages={list.pages} onChange={changePage} />
      )}
    </div>
  );
};

export default ReviewsTab;
